import { ETheme } from "@/common/entity/Enum";
import { HttpResponse } from "@/fetchInterceptor/AxiosMethod";
import { IFetchRequest, SettingService } from "@/services";
import { SettingRequestModel } from "@/services/setting-service/entity/SettingRequestModel";
import {
  DateTimeFormat,
  SettingResponseModel,
} from "@/services/setting-service/entity/SettingResponseModel";
import {
  ActionReducerMapBuilder,
  PayloadAction,
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";
import {
  IDatetimeRequest,
  IDefaultRequest,
  ISettingStore,
  IValueSetting,
} from "./SettingModel";
import { setLanguageAndFormatTime } from "@/common/languages/i18next";
import { changeLanguageDate } from "@/common/languages/moment";

const initialState: ISettingStore = {
  language: "en-gb",
  theme: ETheme.Light,
  isAssistantOrganizer: false,
  emailAssistantOrganizer: "",
  emailFlowAssistantOrganizer: null,
  defaultLocation: "",
  defaultCategories: "",
  dateTimeFormat: new DateTimeFormat(),
  loading: false,
  orderDeadlineStopMovingOrder: false,
};

export const getSettingOrgInfo: any = createAsyncThunk(
  "setting/get-settings",
  async (payload: IFetchRequest, { rejectWithValue }) => {
    const settingServiceImpl: SettingService = new SettingService();

    const response: HttpResponse<SettingResponseModel> =
      await settingServiceImpl.fetchSetting({
        orgId: payload.orgId,
        tenantId: payload.tenantId,
      });
    if (!response.error) {
      if (response.data.language) {
        setLanguageAndFormatTime(response.data.language);
        changeLanguageDate(response.data.language);
      }
      return response.data;
    } else {
      return rejectWithValue(response.errorMsg || "Error");
    }
  }
);

export const settingSlice = createSlice({
  name: "setting",
  initialState,
  reducers: {
    updateLanguage: (
      state: ISettingStore,
      action: PayloadAction<IValueSetting>
    ) => {
      state.language = action.payload.value || "en";

      const settingServiceImpl: SettingService = new SettingService();

      const org = {
        orgId: action.payload.OrganizationInfo.orgId,
        tenantId: action.payload.OrganizationInfo.tenantId,
      };
      const payload = state as SettingRequestModel;
      settingServiceImpl.addSettingOrganization(org, payload);
    },
    updateDatetime: (
      state: ISettingStore,
      action: PayloadAction<IDatetimeRequest>
    ) => {
      state.dateTimeFormat.dateFormat = action.payload.DateFormat;
      state.dateTimeFormat.timeFormat = action.payload.TimeFormat;
      state.dateTimeFormat.firstDayOfWeek = action.payload.FirstDayOfWeek;
      state.dateTimeFormat.dateSeparator = action.payload.DateSeparator;

      const settingServiceImpl: SettingService = new SettingService();

      const org = {
        orgId: action.payload.OrganizationInfo.orgId,
        tenantId: action.payload.OrganizationInfo.tenantId,
      };
      const payload: SettingRequestModel = {
        Language: state.language || "en",
        IsAssistantOrganizer: state.isAssistantOrganizer,
        EmailAssistantOrganizer: state.emailAssistantOrganizer,
        EmailFlowAssistantOrganizer:
          state.emailFlowAssistantOrganizer || undefined,
        FirstDayOfWeek: state.dateTimeFormat.firstDayOfWeek,
        TimeFormat: state.dateTimeFormat.timeFormat,
        DateFormat: state.dateTimeFormat.dateFormat,
        DateSeparator: state.dateTimeFormat.dateSeparator,
        DefaultLocation: state.defaultLocation,
        DefaultCategories: state.defaultCategories,
      };
      settingServiceImpl.addSettingOrganization(org, payload);
    },
    updateDefault: (
      state: ISettingStore,
      action: PayloadAction<IDefaultRequest>
    ) => {
      if (action.payload.type === "LOCATIONS") {
        state.defaultLocation = action.payload.Ids;
      } else {
        state.defaultCategories = action.payload.Ids;
      }
      const settingServiceImpl: SettingService = new SettingService();
      const org = {
        orgId: action.payload.OrganizationInfo.orgId,
        tenantId: action.payload.OrganizationInfo.tenantId,
      };
      const payload = state as SettingRequestModel;
      settingServiceImpl.addSettingOrganization(org, payload);
    },
    setTheme: (state: ISettingStore, action: PayloadAction<string>) => {
      if (action.payload === "#212121") {
        state.theme = ETheme.Dark;
      } else {
        state.theme = action.payload;
      }
    },
    updateSettings: (
      state: ISettingStore,
      action: PayloadAction<Partial<ISettingStore>>
    ) => {
      setLanguageAndFormatTime(action.payload?.language || "en");
      changeLanguageDate(action.payload?.language || "en");
      return { ...state, ...action.payload };
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<ISettingStore>) => {
    builder

      .addCase(getSettingOrgInfo.pending, (state: ISettingStore) => {
        state.loading = true;
      })
      .addCase(
        getSettingOrgInfo.fulfilled,
        (
          state: ISettingStore,
          _action: PayloadAction<SettingResponseModel>
        ) => {
          state.language = _action.payload.language || "en";

          state.isAssistantOrganizer = _action.payload.isAssistantOrganizer;
          state.emailAssistantOrganizer =
            _action.payload.emailAssistantOrganizer;
          state.emailFlowAssistantOrganizer =
            _action.payload.emailFlowAssistantOrganizer || null;
          state.defaultLocation = _action.payload.defaultLocation;
          state.defaultCategories = _action.payload.defaultCategories;
          state.dateTimeFormat = _action.payload.dateTimeFormat;
          state.loading = false;
        }
      )
      .addCase(getSettingOrgInfo.rejected, (state: ISettingStore) => {
        state.loading = false;
      });
  },
});

export const {
  updateLanguage,
  setTheme,
  updateSettings,
  updateDatetime,
  updateDefault,
} = settingSlice.actions;

export default settingSlice.reducer;
